/** 云端资料合并策略：更新网站版本时只 patch 字段，不整表覆盖、不用空值冲掉已有数据 */

export const mergeUniqueStringArrays = (primary = [], secondary = []) => {
  const a = Array.isArray(primary) ? primary : [];
  const b = Array.isArray(secondary) ? secondary : [];
  return [...new Set([...a, ...b].map(String))];
};

export const pickNonEmptyString = (preferred, fallback = '') => {
  const value = preferred == null ? '' : String(preferred);
  if (value.trim()) return preferred;
  const backup = fallback == null ? '' : String(fallback);
  return backup.trim() ? fallback : preferred ?? fallback ?? '';
};

export const isAutoGeneratedUserName = (name, phone) => {
  const normalized = String(name || '').trim();
  if (!normalized) return true;
  const phoneStr = String(phone || '');
  if (phoneStr && normalized === `用户_${phoneStr.slice(-4)}`) return true;
  return false;
};

export const profileFieldTimestamp = (profile = {}) =>
  Number(profile?.profileUpdatedAt || profile?.updatedAt || 0);

/** 按更新时间合并资料字段，云端较新时优先（跨设备登录以云为准） */
export const pickProfileField = (field, localProfile = {}, cloudDoc = {}) => {
  const localTs = profileFieldTimestamp(localProfile);
  const cloudTs = profileFieldTimestamp(cloudDoc);
  const localVal = localProfile?.[field];
  const cloudVal = cloudDoc?.[field];
  const phone = localProfile?.phone || cloudDoc?.phone;

  if (field === 'name' && isAutoGeneratedUserName(localVal, phone) && String(cloudVal || '').trim()) {
    return cloudVal;
  }

  if (cloudTs > localTs) return pickNonEmptyString(cloudVal, localVal);
  if (localTs > cloudTs) return pickNonEmptyString(localVal, cloudVal);
  if (String(cloudVal || '').trim()) return cloudVal;
  return pickNonEmptyString(localVal, cloudVal);
};

export const profilesDiffer = (left = {}, right = {}, fields = []) =>
  fields.some((field) => JSON.stringify(left?.[field]) !== JSON.stringify(right?.[field]));

export const appendLegacyUids = (existingLegacy = [], ...candidates) => {
  const merged = new Set(
    (Array.isArray(existingLegacy) ? existingLegacy : [])
      .map(String)
      .filter(Boolean)
  );
  candidates
    .map((item) => (item == null ? '' : String(item)))
    .filter(Boolean)
    .forEach((item) => merged.add(item));
  return [...merged].slice(-12);
};

/**
 * 写入 users 集合前的安全合并：本地 + 云端取并集/非空优先，避免升级后把关注、头像等冲掉。
 */
export const mergeSafeUserPayload = (cloudDoc = {}, localProfile = {}) => {
  const cloud = cloudDoc || {};
  const local = localProfile || {};

  const uid =
    pickNonEmptyString(local.uid, cloud.uid) ||
    pickNonEmptyString(cloud.uid, local.uid) ||
    '';

  const legacyUids = appendLegacyUids(
    mergeUniqueStringArrays(cloud.legacyUids, local.legacyUids),
    cloud.uid !== uid ? cloud.uid : null,
    local.uid !== uid ? local.uid : null
  );

  return {
    name: pickProfileField('name', local, cloud),
    phone: pickNonEmptyString(local.phone, cloud.phone),
    uid,
    legacyUids,
    bio: pickProfileField('bio', local, cloud),
    avatar: pickProfileField('avatar', local, cloud),
    coverImg: pickProfileField('coverImg', local, cloud),
    location: pickProfileField('location', local, cloud),
    city: pickProfileField('city', local, cloud),
    province: pickProfileField('province', local, cloud),
    locationData: local.locationData ?? cloud.locationData ?? null,
    locationSource: pickProfileField('locationSource', local, cloud),
    locationAgreed: local.locationAgreed ?? cloud.locationAgreed ?? false,
    role: pickNonEmptyString(local.role, cloud.role) || 'user',
    isVerified: local.isVerified ?? cloud.isVerified ?? false,
    followingList: mergeUniqueStringArrays(local.followingList, cloud.followingList),
    followers: Math.max(Number(local.followers || 0), Number(cloud.followers || 0)),
    profileUpdatedAt: Math.max(
      Number(local.profileUpdatedAt || 0),
      Number(cloud.profileUpdatedAt || 0)
    ) || Date.now(),
    createdAt: cloud.createdAt || local.createdAt || Date.now(),
    updatedAt: Date.now()
  };
};

/** 帖子/领养互动字段 patch：只更新传入字段，保留其余云端数据 */
export const mergeSafePostPatch = (existing = {}, patch = {}) => {
  const base = existing || {};
  const next = { ...base, ...patch, updatedAt: Date.now() };
  if (patch.likedBy || base.likedBy) {
    next.likedBy = patch.likedBy ?? base.likedBy ?? [];
    next.likes = Array.isArray(next.likedBy) ? next.likedBy.length : Number(next.likes || 0);
  }
  if (patch.bookmarkedBy || base.bookmarkedBy) {
    next.bookmarkedBy = patch.bookmarkedBy ?? base.bookmarkedBy ?? [];
  }
  if (patch.comments || base.comments) {
    next.comments = patch.comments ?? base.comments ?? [];
    next.commentsCount = Array.isArray(next.comments) ? next.comments.length : Number(next.commentsCount || 0);
  }
  return next;
};

export const actorKeysForProfile = (profile) => {
  if (!profile) return [];
  return [
    ...new Set(
      [
        profile.uid,
        profile.phone,
        profile.id,
        profile.userId,
        profile._id,
        ...(Array.isArray(profile.legacyUids) ? profile.legacyUids : [])
      ]
        .filter(Boolean)
        .map(String)
    )
  ];
};

export const profileMatchesActorKey = (profile, actorKey) => {
  if (!profile || actorKey == null || actorKey === '') return false;
  const key = String(actorKey);
  return actorKeysForProfile(profile).some((value) => value === key);
};
